/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Publish the context resolved from the audience login URL to every module.
 * @state-owner audience route plus auth module active-context query; this context is a read-only projection
 * @allowed-imports React; shared view types
 * @forbidden role persistence; in-product role switching; authorization decisions
 * @public-api workspace routes and modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import type { WorkspaceRole } from '../../shared/lib/model';

export interface ActiveWorkspaceContext {
  readonly role: WorkspaceRole;
  /** The access-context id every module query key is scoped by. */
  readonly contextId: string;
  /** Backend-authoritative organization scope. Never derive this from the opaque context id. */
  readonly organizationId: string | null;
}

const ActiveContext = createContext<ActiveWorkspaceContext | null>(null);

export function ActiveRoleProvider({
  children,
  contextId,
  organizationId = null,
  role,
}: PropsWithChildren<{
  contextId: string;
  organizationId?: string | null;
  role: WorkspaceRole;
}>) {
  const value = useMemo(
    () => ({ contextId, organizationId, role }),
    [contextId, organizationId, role],
  );
  return <ActiveContext.Provider value={value}>{children}</ActiveContext.Provider>;
}

export function useActiveWorkspaceContext(): ActiveWorkspaceContext {
  const value = useContext(ActiveContext);
  if (!value) throw new Error('ActiveRoleProvider is missing');
  return value;
}

export function useActiveRole(): WorkspaceRole {
  return useActiveWorkspaceContext().role;
}

export function useActiveContextId(): string {
  return useActiveWorkspaceContext().contextId;
}

export function useActiveOrganizationId(): string | null {
  return useActiveWorkspaceContext().organizationId;
}

export function useIsTeacher(): boolean {
  return useActiveRole() === 'teacher';
}
